import {Table, Badge} from '@goproblems/ui';
import {DashboardTableDate} from '@components/DashboardTableContent';
import {Button} from '@goproblems/ui';
import {UserItem} from '@components';
import {useTranslation} from 'react-i18next';
import dayjs from 'dayjs';
import {getShortLevelName, getTournamentPath} from '@utils';
import {TournamentMinimal, TournamentStatus} from '@types';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {faCopy, faEdit, faEye} from '@fortawesome/free-regular-svg-icons';
import {faSort, faSortUp, faSortDown} from '@fortawesome/free-solid-svg-icons';
import {Link, useNavigate} from 'react-router';
import {toast} from 'react-toastify';

export const TournamentTable = ({
    tournaments,
    onClick,
    showActions,
    onSort,
    sortBy,
    sortDirection,
    dashboard = false,
}: {
    tournaments: TournamentMinimal[];
    onClick: (tournamentId: number) => void;
    showActions?: boolean;
    onSort?: (sortBy: string) => void;
    sortBy?: string;
    sortDirection?: 'asc' | 'desc';
    dashboard?: boolean;
}) => {
    const {t} = useTranslation();
    const navigate = useNavigate();

    const getSortIcon = (column: string) => {
        const isActiveColumn = sortBy === column;
        if (!isActiveColumn) {
            return (
                <FontAwesomeIcon icon={faSort} className="ms-2 text-muted" />
            );
        }

        return (
            <FontAwesomeIcon
                icon={sortDirection === 'asc' ? faSortUp : faSortDown}
                className="ms-2 text-muted"
            />
        );
    };

    const handleSort = (column: string) => {
        if (onSort) {
            onSort(column);
        }
    };

    const renderSortableHeader = (
        title: string,
        column: string,
        className = ''
    ) => {
        if (!onSort) {
            return <th className={className}>{title}</th>;
        }
        return (
            <th
                className={className}
                aria-sort={
                    sortBy === column
                        ? sortDirection === 'asc'
                            ? 'ascending'
                            : 'descending'
                        : 'none'
                }>
                <button
                    type="button"
                    className="tournament-sort-heading"
                    onClick={() => handleSort(column)}>
                    {title}
                    {getSortIcon(column)}
                </button>
            </th>
        );
    };

    return (
        <Table
            hover
            responsive
            className={
                dashboard
                    ? 'dashboard-tournaments-table'
                    : 'tournament-list-table'
            }>
            <thead>
                <tr>
                    <th className="text-left">{t('common.id')}</th>
                    {renderSortableHeader(
                        t('tournaments.started_at'),
                        'started_at',
                        'text-left'
                    )}
                    <th className="text-left">{t('tournaments.status')}</th>
                    {renderSortableHeader(
                        dashboard
                            ? t('tournaments.players_count', 'Players')
                            : t('tournaments.number_of_players'),
                        'players_count',
                        'text-left'
                    )}
                    {renderSortableHeader(
                        dashboard
                            ? t('tournaments.rounds_count', 'Rounds')
                            : t('tournaments.number_of_rounds'),
                        'rounds_count'
                    )}
                    {!dashboard && <th>{t('tournaments.ruleset')}</th>}
                    <th>{t('tournaments.base_level')}</th>
                    <th>{t('tournaments.winner')}</th>
                    {showActions && <th>{t('tournaments.actions')}</th>}
                </tr>
            </thead>
            <tbody>
                {tournaments.map(tournament => (
                    <tr
                        className="cursor-pointer"
                        key={tournament.id}
                        onClick={() => onClick(tournament.id)}>
                        <td className="bg-inherit">
                            {dashboard ? (
                                <strong>#{tournament.id}</strong>
                            ) : (
                                <Link
                                    to={getTournamentPath(tournament)}
                                    onClick={event => event.stopPropagation()}
                                    className="tournament-id">
                                    #{tournament.id}
                                </Link>
                            )}
                        </td>
                        <td data-label={t('tournaments.started_at')}>
                            {dashboard ? (
                                <DashboardTableDate
                                    value={tournament.startedAt}
                                    showTime
                                />
                            ) : (
                                dayjs(tournament.startedAt).format(
                                    'MMM D, YYYY · HH:mm'
                                )
                            )}
                        </td>
                        <td data-label={t('tournaments.status')}>
                            {dashboard ? (
                                <Badge bg="secondary">
                                    {t(
                                        `tournaments.${tournament.status}`,
                                        tournament.status
                                    )}
                                </Badge>
                            ) : (
                                <span
                                    className={`tournament-status is-${tournament.status}`}>
                                    {t(
                                        `tournaments.${tournament.status}`,
                                        tournament.status
                                    )}
                                </span>
                            )}
                        </td>
                        <td data-label={t('tournaments.entrants')}>
                            {tournament.playersCount}
                        </td>
                        <td data-label={t('tournaments.rounds')}>
                            {tournament.roundsCount}
                        </td>
                        {!dashboard && (
                            <td data-label={t('tournaments.ruleset')}>
                                {t(
                                    `tournaments.rulesets.${tournament.ruleset}`
                                )}
                            </td>
                        )}
                        <td data-label={t('tournaments.base_level')}>
                            {getShortLevelName(
                                tournament.rulesetConfiguration.baseLevel
                            )}
                        </td>
                        <td
                            data-label={t('tournaments.winner')}
                            onClick={e => e.stopPropagation()}>
                            {tournament.winners.length === 0 &&
                                (tournament.status === TournamentStatus.Finished
                                    ? t('tournaments.no_winner')
                                    : '—')}
                            {tournament.winners.length > 0 &&
                                tournament.winners.map(winner => (
                                    <UserItem
                                        key={winner.id}
                                        avatarClassName="ml-0"
                                        user={winner}
                                    />
                                ))}
                        </td>
                        {showActions && (
                            <td onClick={e => e.stopPropagation()}>
                                <div className="tournament-table-actions">
                                    <Button
                                        variant="light"
                                        size="sm"
                                        title={t('common.view')}
                                        onClick={() =>
                                            navigate(
                                                getTournamentPath(tournament)
                                            )
                                        }>
                                        <FontAwesomeIcon
                                            icon={faEye}
                                            className="me-1"
                                        />
                                        {t('common.view')}
                                    </Button>
                                    {tournament.status ===
                                        TournamentStatus.Scheduled && (
                                        <>
                                            <Button
                                                variant="light"
                                                size="sm"
                                                title={t('common.edit')}
                                                onClick={() =>
                                                    navigate(
                                                        `/tournaments/${tournament.id}/edit`
                                                    )
                                                }>
                                                <FontAwesomeIcon
                                                    icon={faEdit}
                                                    className="me-1"
                                                />
                                                {t('common.edit')}
                                            </Button>
                                            <Button
                                                variant="light"
                                                size="sm"
                                                title={t('common.share')}
                                                onClick={() => {
                                                    const url = `${window.location.origin}/tournaments/${tournament.id}/lobby`;
                                                    void navigator.clipboard.writeText(
                                                        url
                                                    );
                                                    toast.success(
                                                        t(
                                                            'tournaments.link_copied'
                                                        )
                                                    );
                                                }}>
                                                <FontAwesomeIcon
                                                    icon={faCopy}
                                                    className="me-1"
                                                />
                                                {t('tournaments.copy_link')}
                                            </Button>
                                        </>
                                    )}
                                </div>
                            </td>
                        )}
                    </tr>
                ))}
            </tbody>
        </Table>
    );
};
